import React, { useEffect, useMemo, useState } from 'react';
import { ChartBarIcon, DocumentArrowDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds, useMasterData } from '../../hooks/useApi';
import apiClient from '../../utils/api';

const CycleCount: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [selectedPondId, setSelectedPondId] = useState<string>('');
  const [fishSpecies, setFishSpecies] = useState<string>('');
  const [countDate, setCountDate] = useState<string>('');
  const [biomassKg, setBiomassKg] = useState<string>('');
  const [fishCount, setFishCount] = useState<string>('');
  const [avgWeightG, setAvgWeightG] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [attachmentUrl, setAttachmentUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [refreshingValues, setRefreshingValues] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [currentValues, setCurrentValues] = useState<any>(null);
  const [auditHistory, setAuditHistory] = useState<any[]>([]);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { data: fishSpeciesData, loading: fishSpeciesLoading, error: fishSpeciesError } = useMasterData('fish_species', selectedLocation?.id || null);
  const pondOptions = useMemo(() => ponds || [], [ponds]);
  const fishSpeciesOptions = useMemo(() => fishSpeciesData || [], [fishSpeciesData]);

  // Debug logging for fish species data
  useEffect(() => {
    if (fishSpeciesError) {
      console.error('Error loading fish species:', fishSpeciesError);
    }
    if (fishSpeciesData) {
      console.log('Fish species data loaded:', fishSpeciesData);
    }
  }, [fishSpeciesData, fishSpeciesError]);

  // Helper function to normalize species names to match master data casing
  const normalizeSpeciesName = (species: string): string => {
    const normalized = species.toLowerCase().trim();
    const masterDataMapping: Record<string, string> = {
      'rohu': 'Rohu',
      'catla': 'Catla', 
      'mrigal': 'Mrigal',
      'grass carp': 'Grass Carp',
      'silver carp': 'Silver Carp',
      'bighead carp': 'Bighead Carp',
      'tilapia': 'Tilapia',
      'pangash': 'Pangash',
      'pangasius': 'Pangasius',
      'common carp': 'Common Carp'
    };
    return masterDataMapping[normalized] || species;
  };

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    setCountDate(today);
  }, []);

  useEffect(() => {
    if (selectedPondId && selectedLocation) {
      fetchCurrentValues();
      fetchAuditHistory();
    }
  }, [selectedPondId, selectedLocation]);

  const fetchCurrentValues = async () => {
    if (!selectedPondId || !selectedLocation) return;
    setRefreshingValues(true);
    try {
      // Fetch fresh pond data from API instead of using cached data
      const response = await apiClient.get(`/locations/${selectedLocation.id}/ponds`);
      const ponds = response.data || [];
      const pond = ponds.find((p: any) => p.id === selectedPondId);
      
      if (pond) {
        // Calculate totals from species inventory if available
        let totalBiomass = 0;
        let totalFishCount = 0;
        
        if (pond.species_inventory && Object.keys(pond.species_inventory).length > 0) {
          Object.values(pond.species_inventory).forEach((species: any) => {
            totalBiomass += species.biomass_kg || 0;
            totalFishCount += species.count || 0;
          });
        } else {
          // Fallback to legacy values (should not happen with new system)
          totalBiomass = 0;
          totalFishCount = 0;
        }
        
        setCurrentValues({
          biomass_kg: totalBiomass,
          fish_count: totalFishCount,
          last_source: pond.last_biomass_source || 'counter',
          last_updated: pond.last_biomass_updated_at || 'Never',
          species_inventory: pond.species_inventory || {}
        });
      }
    } catch (e) {
      console.error('Failed to fetch current values:', e);
    } finally {
      setRefreshingValues(false);
    }
  };

  const fetchAuditHistory = async () => {
    if (!selectedLocation) return;
    try {
      const res = await apiClient.get('/reconcile/logs', {
        params: {
          location_id: selectedLocation.id,
          pond_id: selectedPondId || undefined,
        },
      });
      setAuditHistory(res.data || []);
    } catch (e) {
      console.error('Failed to fetch audit history:', e);
    }
  };

  const extractErrorMessage = (err: any): string => {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message || 'Something went wrong';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ');
    }
    if (typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }
    return String(detail);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocation || !selectedPondId || !fishSpecies || !countDate || !fishCount || !avgWeightG || !reason) {
      setError('Please fill in all required fields');
      return;
    }

    // Auto-calculate biomass from fish count and average weight
    const fishCountNum = parseInt(fishCount);
    const avgWeightNum = parseFloat(avgWeightG);
    const calculatedBiomass = (fishCountNum * avgWeightNum) / 1000;
    
    // Validation: Check for unrealistic values
    if (avgWeightNum > 1500) { // More than 1.5kg per fish
      const confirmProceed = window.confirm(
        `⚠️ Warning: The average weight is ${avgWeightNum}g per fish.\n\n` +
        `This seems unusually high for most fish species. Typical ranges:\n` +
        `• Small fish: 50-200g\n` +
        `• Medium fish: 200-500g\n` +
        `• Large fish: 500-1000g\n\n` +
        `Are you sure this value is correct?`
      );
      if (!confirmProceed) {
        return;
      }
    }

    if (avgWeightNum < 10) { // Less than 10g per fish
      const confirmProceed = window.confirm(
        `⚠️ Warning: The average weight is ${avgWeightNum}g per fish.\n\n` +
        `This seems unusually low. Are you sure this value is correct?`
      );
      if (!confirmProceed) {
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        location_id: selectedLocation.id,
        pond_id: selectedPondId,
        count_date: countDate,
        fish_species: fishSpecies,
        biomass_kg: calculatedBiomass,
        fish_count: fishCountNum,
        avg_weight_g: avgWeightNum,
        reason,
        attachment_url: attachmentUrl || undefined,
      };

      await apiClient.post('/reconcile/cycle-count', payload);
      
      setSuccess('Cycle count recorded successfully');
      setBiomassKg('');
      setFishCount('');
      setAvgWeightG('');
      setFishSpecies('');
      setReason('');
      setAttachmentUrl('');
      
      // Refresh current values and audit history
      fetchCurrentValues();
      fetchAuditHistory();
    } catch (e: any) {
      setError(extractErrorMessage(e) || 'Failed to record cycle count');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="flex items-center gap-2">
        <ChartBarIcon className="h-6 w-6 text-orange-600" />
        <h1 className="text-2xl font-bold text-gray-900">Cycle Count (Manual Reconciliation)</h1>
      </div>

      <div className="bg-orange-50 border border-orange-100 text-orange-800 text-sm rounded-lg p-3">
        <div className="font-medium mb-1">Purpose</div>
        <p>Reconcile key pond values when operations deviate from normal workflows. Use for start/end of cycle baselining or mid-cycle corrections.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Record Cycle Count</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pond *</label>
              <select 
                value={selectedPondId} 
                onChange={(e) => setSelectedPondId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              >
                <option value="">Select a pond</option>
                {pondOptions.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fish Species *</label>
              {fishSpeciesLoading ? (
                <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500 text-sm">
                  Loading fish species...
                </div>
              ) : fishSpeciesError ? (
                <div className="space-y-2">
                  <select 
                    value={fishSpecies} 
                    onChange={(e) => setFishSpecies(e.target.value)}
                    className="w-full px-3 py-2 border border-red-300 rounded-lg bg-red-50"
                    required
                  >
                    <option value="">Error loading species - Select manually</option>
                  </select>
                  <div className="text-red-600 text-xs flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-3 w-3" />
                    Failed to load fish species: {typeof fishSpeciesError === 'string' ? fishSpeciesError : 'Validation error – please check master data or try again.'}
                  </div>
                </div>
              ) : fishSpeciesOptions.length === 0 ? (
                <div className="space-y-2">
                  <select 
                    value={fishSpecies} 
                    onChange={(e) => setFishSpecies(e.target.value)}
                    className="w-full px-3 py-2 border border-yellow-300 rounded-lg bg-yellow-50"
                    required
                  >
                    <option value="">No fish species available</option>
                  </select>
                  <div className="text-yellow-600 text-xs">
                    No fish species found in master data. Please add species in Admin → Master Data.
                  </div>
                </div>
              ) : (
                <select 
                  value={fishSpecies} 
                  onChange={(e) => setFishSpecies(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select species</option>
                  {fishSpeciesOptions.map((species: any) => (
                    <option key={species.id} value={species.name}>{species.name}</option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Count Date *</label>
              <input 
                type="date" 
                value={countDate} 
                onChange={(e) => setCountDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fish Count *</label>
              <input 
                type="number" 
                min="1"
                value={fishCount} 
                onChange={(e) => {
                  setFishCount(e.target.value);
                  // Auto-calculate biomass when fish count changes
                  if (e.target.value && avgWeightG) {
                    const calculatedBiomass = (parseInt(e.target.value) * parseFloat(avgWeightG)) / 1000;
                    setBiomassKg(calculatedBiomass.toFixed(2));
                  }
                }}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter fish count"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Average Weight (g) *</label>
              <input 
                type="number" 
                step="0.1" 
                min="1"
                value={avgWeightG} 
                onChange={(e) => {
                  setAvgWeightG(e.target.value);
                  // Auto-calculate biomass when average weight changes
                  if (e.target.value && fishCount) {
                    const calculatedBiomass = (parseInt(fishCount) * parseFloat(e.target.value)) / 1000;
                    setBiomassKg(calculatedBiomass.toFixed(2));
                  }
                }}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter average weight in grams"
                required
              />
            </div>

            {/* Auto-calculated Biomass Display */}
            {fishCount && avgWeightG && parseFloat(fishCount) > 0 && parseFloat(avgWeightG) > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-sm text-green-800">
                  <strong>Auto-calculated Biomass:</strong> {((parseInt(fishCount) * parseFloat(avgWeightG)) / 1000).toFixed(2)} kg
                  <div className="text-xs text-green-600 mt-1">
                    Formula: ({fishCount} fish × {avgWeightG}g) ÷ 1000 = {((parseInt(fishCount) * parseFloat(avgWeightG)) / 1000).toFixed(2)} kg
                  </div>
                </div>
              </div>
            )}

            {/* Hidden biomass input for form submission */}
            <input type="hidden" value={biomassKg} />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
              <textarea 
                value={reason} 
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                placeholder="Explain why this reconciliation is needed"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attachment URL (optional)</label>
              <input 
                type="url" 
                value={attachmentUrl} 
                onChange={(e) => setAttachmentUrl(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Link to evidence/documentation"
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm flex items-center gap-2">
                <ExclamationTriangleIcon className="h-4 w-4" />
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-600 text-sm">
                {success}
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading}
              className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {loading ? 'Recording...' : 'Record Cycle Count'}
            </button>
          </form>
        </div>

        {/* Current Values & History */}
        <div className="space-y-6">
          {/* Current Values */}
          {currentValues && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Current Values</h2>
                {refreshingValues && (
                  <div className="text-sm text-gray-500 flex items-center gap-1">
                    <div className="animate-spin h-3 w-3 border border-gray-300 border-t-gray-600 rounded-full"></div>
                    Refreshing...
                  </div>
                )}
              </div>
              
              {/* Species Inventory */}
              {currentValues.species_inventory && Object.keys(currentValues.species_inventory).length > 0 ? (
                <div className="space-y-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">Species Inventory:</div>
                  {(() => {
                    // Normalize and merge species data
                    const normalizedSpecies: Record<string, any> = {};
                    
                    Object.entries(currentValues.species_inventory).forEach(([species, data]: [string, any]) => {
                      const normalizedName = normalizeSpeciesName(species);
                      
                      if (normalizedName in normalizedSpecies) {
                        // Merge with existing species
                        const existing = normalizedSpecies[normalizedName];
                        normalizedSpecies[normalizedName] = {
                          count: (existing.count || 0) + (data.count || 0),
                          biomass_kg: (existing.biomass_kg || 0) + (data.biomass_kg || 0),
                          avg_weight_g: existing.avg_weight_g || data.avg_weight_g || 0,
                          last_updated: existing.last_updated || data.last_updated
                        };
                      } else {
                        // New species
                        normalizedSpecies[normalizedName] = {
                          count: data.count || 0,
                          biomass_kg: data.biomass_kg || 0,
                          avg_weight_g: data.avg_weight_g || 0,
                          last_updated: data.last_updated
                        };
                      }
                    });
                    
                    return Object.entries(normalizedSpecies)
                      .filter(([species, data]: [string, any]) => {
                        // Only show species that have at least one non-zero value
                        return (data.count || 0) > 0 || (data.biomass_kg || 0) > 0;
                      })
                      .map(([species, data]: [string, any]) => (
                        <div key={species} className="bg-gray-50 rounded-lg p-3">
                          <div className="font-medium text-gray-900 mb-2">{species}</div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Count:</span>
                              <span className="font-medium">{data.count || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Biomass:</span>
                              <span className="font-medium">{data.biomass_kg || 0} kg</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Avg Weight:</span>
                              <span className="font-medium">
                                {(data.count || 0) > 0 && (data.biomass_kg || 0) > 0 
                                  ? (data.avg_weight_g || 0) 
                                  : 0} g
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Updated:</span>
                              <span className="font-medium text-xs">
                                {data.last_updated ? new Date(data.last_updated).toLocaleDateString() : 'Never'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ));
                  })()}
                  {(() => {
                    // Check if there are any active species after normalization
                    const normalizedSpecies: Record<string, any> = {};
                    
                    Object.entries(currentValues.species_inventory).forEach(([species, data]: [string, any]) => {
                      const normalizedName = normalizeSpeciesName(species);
                      
                      if (normalizedName in normalizedSpecies) {
                        const existing = normalizedSpecies[normalizedName];
                        normalizedSpecies[normalizedName] = {
                          count: (existing.count || 0) + (data.count || 0),
                          biomass_kg: (existing.biomass_kg || 0) + (data.biomass_kg || 0),
                          avg_weight_g: existing.avg_weight_g || data.avg_weight_g || 0,
                          last_updated: existing.last_updated || data.last_updated
                        };
                      } else {
                        normalizedSpecies[normalizedName] = {
                          count: data.count || 0,
                          biomass_kg: data.biomass_kg || 0,
                          avg_weight_g: data.avg_weight_g || 0,
                          last_updated: data.last_updated
                        };
                      }
                    });
                    
                    const activeSpecies = Object.entries(normalizedSpecies).filter(([species, data]: [string, any]) => {
                      return (data.count || 0) > 0 || (data.biomass_kg || 0) > 0;
                    });
                    
                    return activeSpecies.length === 0 && (
                      <div className="text-center text-gray-500 text-sm py-4">
                        No active species inventory (all species have been sold/removed)
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Biomass (kg):</span>
                    <span className="font-medium">{currentValues.biomass_kg}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Fish Count:</span>
                    <span className="font-medium">{currentValues.fish_count}</span>
                  </div>
                </div>
              )}
              
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Last Source:</span>
                  <span className="font-medium capitalize">{currentValues.last_source}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="font-medium">{currentValues.last_updated}</span>
                </div>
              </div>
            </div>
          )}

          {/* Audit History */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Audit History</h2>
            {auditHistory.length === 0 ? (
              <p className="text-gray-500 text-sm">No reconciliation history found</p>
            ) : (
              <div className="space-y-4 max-h-80 overflow-y-auto">
                {auditHistory.map((log: any, idx: number) => (
                  <div key={idx} className="border-l-4 border-orange-300 pl-4 py-3 bg-orange-50 rounded-r-lg">
                    {/* Header with date and species */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-gray-900">
                        {new Date(log.count_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                      {log.fish_species && (
                        <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                          {log.fish_species}
                        </div>
                      )}
                    </div>
                    
                    {/* Species-specific details */}
                    {log.fish_species && (
                      <div className="mb-3 p-2 bg-white rounded border border-orange-200">
                        <div className="text-xs font-medium text-gray-700 mb-1">Species Details:</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Count:</span>
                            <span className="font-medium">{log.after_fish_count || 0} fish</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Avg Weight:</span>
                            <span className="font-medium">{log.avg_weight_g || 0}g</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Biomass changes */}
                    <div className="mb-2">
                      <div className="text-xs font-medium text-gray-700 mb-1">Biomass Change:</div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-600">Before:</span>
                        <span className="font-medium">{log.before_biomass_kg} kg</span>
                        <span className="text-gray-400">→</span>
                        <span className="text-gray-600">After:</span>
                        <span className="font-medium text-green-600">{log.after_biomass_kg} kg</span>
                        <span className={`text-xs px-1 py-0.5 rounded ${
                          log.after_biomass_kg > log.before_biomass_kg 
                            ? 'bg-green-100 text-green-700' 
                            : log.after_biomass_kg < log.before_biomass_kg 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {log.after_biomass_kg > log.before_biomass_kg ? '+' : ''}
                          {(log.after_biomass_kg - log.before_biomass_kg).toFixed(1)} kg
                        </span>
                      </div>
                    </div>
                    
                    {/* Fish count changes */}
                    {log.before_fish_count !== undefined && log.after_fish_count !== undefined && (
                      <div className="mb-2">
                        <div className="text-xs font-medium text-gray-700 mb-1">Fish Count Change:</div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-600">Before:</span>
                          <span className="font-medium">{log.before_fish_count} fish</span>
                          <span className="text-gray-400">→</span>
                          <span className="text-gray-600">After:</span>
                          <span className="font-medium text-green-600">{log.after_fish_count} fish</span>
                          <span className={`text-xs px-1 py-0.5 rounded ${
                            log.after_fish_count > log.before_fish_count 
                              ? 'bg-green-100 text-green-700' 
                              : log.after_fish_count < log.before_fish_count 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {log.after_fish_count > log.before_fish_count ? '+' : ''}
                            {log.after_fish_count - log.before_fish_count} fish
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Reason */}
                    <div className="text-xs text-gray-600 bg-white p-2 rounded border border-orange-200">
                      <span className="font-medium">Reason:</span> {log.reason}
                    </div>
                    
                    {/* Timestamp */}
                    <div className="text-xs text-gray-500 mt-2">
                      {new Date(log.created_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CycleCount;
